// core/data/communities.ts — Typed seed for communities + invites.
//
// Faithful port of legacy src/scripts/data/communities.js.
// Communities are the 9 hub records. CommunityInvites are the 3 invite rail records.
// `joined` is the only live field (drives My Communities filter).

import type { Community, CommunityInvite } from '@/core/models/community';

export const seedCommunities: Community[] = [
  {
    id: 'ai-future', name: 'AI & Future', category: 'Technology',
    members: '142K members', membersNum: 142000, postsCount: '3.2K', online: 1240,
    color: '#5b6bff', coverTone: 'a',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#9db4ff" stroke-width="1.8"><rect x="5" y="7" width="14" height="11" rx="2"/><path d="M9 7V4M15 7V4M9.5 12h.01M14.5 12h.01M9.5 15.5h5"/></svg>',
    tagline: 'The next leap forward: innovate, collaborate, elevate.',
    description: 'A community for the future of AI: robotics, automation, ethics, and emerging technologies.',
    joined: true, trending: true, recommended: false,
    rules: ['Be respectful and constructive', 'No spam or self-promotion', 'Cite credible sources', 'Keep discussion on-topic'],
    moderators: [{ name: 'WayneTech', av: 'av-grad' }, { name: 'OpenMind', av: 'av-3' }],
    events: [{ title: 'AI & Ethics AMA', date: 'May 28' }, { title: 'Builders Demo Night', date: 'Jun 4' }],
    postIds: ['post-004', 'post-005', 'post-003'],
  },
  {
    id: 'freedom-fighters', name: 'Freedom Fighters', category: 'Activism',
    members: '128K members', membersNum: 128000, postsCount: '5.1K', online: 980,
    color: '#3b6bff', coverTone: 'b',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#7ba0ff" stroke-width="1.8"><circle cx="12" cy="6" r="2.4"/><circle cx="5" cy="17" r="2.4"/><circle cx="19" cy="17" r="2.4"/><path d="M12 8.4 6.8 15M12 8.4 17.2 15M7.2 17h9.6"/></svg>',
    tagline: 'Speak freely. Stand together. Stay unbreakable.',
    description: 'Defenders of open dialogue and the right to speak without fear. Free speech first, always.',
    joined: true, trending: false, recommended: false,
    rules: ['Free speech is the default', 'No direct threats or coordinated abuse', 'Argue ideas, not people', 'Sources welcome, spam is not'],
    moderators: [{ name: 'FreedomSeeker', av: 'av-1' }, { name: 'TruthWarrior', av: 'av-4' }],
    events: [{ title: 'Open Mic: Free Speech', date: 'May 30' }, { title: 'Town Hall Debate', date: 'Jun 8' }],
    postIds: ['post-001', 'post-004', 'post-002'],
  },
  {
    id: 'open-minds', name: 'Open Minds', category: 'Science',
    members: '96K members', membersNum: 96000, postsCount: '2.8K', online: 620,
    color: '#5b54ff', coverTone: 'c',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#9b9bff" stroke-width="1.8"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 3v18M4 9h16M4 15h16"/></svg>',
    tagline: 'Question everything. Discover anything.',
    description: 'Curious people exploring science, philosophy, and ideas worth changing your mind about.',
    joined: false, trending: true, recommended: false,
    rules: ['Curiosity over certainty', 'Back claims with evidence', 'Disagree in good faith', 'No pseudoscience peddling'],
    moderators: [{ name: 'OpenMind', av: 'av-3' }, { name: 'LibertyMind', av: 'av-2' }],
    events: [{ title: 'Science vs Myth Night', date: 'Jun 1' }, { title: 'Philosophy Roundtable', date: 'Jun 11' }],
    postIds: ['post-003', 'post-002', 'post-005'],
  },
  {
    id: 'truth-seekers', name: 'Truth Seekers', category: 'News',
    members: '75K members', membersNum: 75000, postsCount: '4.4K', online: 540,
    color: '#7a3bff', coverTone: 'd',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#c08bff" stroke-width="1.8"><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z"/><path d="m9 12 2 2 4-4"/></svg>',
    tagline: 'Follow the facts, wherever they lead.',
    description: 'Independent investigation and fact-driven discussion. We chase truth, not narratives.',
    joined: false, trending: true, recommended: false,
    rules: ['Verify before you amplify', 'No scams or fraud', 'Cite primary sources', 'Corrections are encouraged'],
    moderators: [{ name: 'TruthWarrior', av: 'av-4' }, { name: 'FreedomSeeker', av: 'av-1' }],
    events: [{ title: 'Fact-Check Workshop', date: 'Jun 2' }, { title: 'Investigations AMA', date: 'Jun 14' }],
    postIds: ['post-002', 'post-001', 'post-004'],
  },
  {
    id: 'creative-works', name: 'Creative Works', category: 'Art',
    members: '63K members', membersNum: 63000, postsCount: '6.7K', online: 710,
    color: '#f2385a', coverTone: 'e',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#ff8bb0" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><circle cx="8.5" cy="9" r="1"/><circle cx="15.5" cy="9" r="1"/><circle cx="9" cy="15" r="1"/><path d="M12 21a3 3 0 0 0 0-6"/></svg>',
    tagline: 'Make something only you could make.',
    description: 'A home for artists, designers, and makers to share work, swap feedback, and collaborate.',
    joined: false, trending: false, recommended: true,
    rules: ['Credit original creators', 'Constructive critique only', 'No reposting without permission', 'Keep it your own work'],
    moderators: [{ name: 'OpenDialogue', av: 'av-5' }, { name: 'WayneTech', av: 'av-grad' }],
    events: [{ title: 'Portfolio Review', date: 'Jun 3' }, { title: 'Live Sketch Jam', date: 'Jun 12' }],
    postIds: ['post-003', 'post-005'],
  },
  {
    id: 'game-on', name: 'Game On', category: 'Gaming',
    members: '210K members', membersNum: 210000, postsCount: '9.3K', online: 3200,
    color: '#28d17c', coverTone: 'f',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#7be3a8" stroke-width="1.8"><rect x="3" y="8" width="18" height="9" rx="4"/><path d="M8 11v3M6.5 12.5h3M15.5 12h.01M17.5 13.5h.01"/></svg>',
    tagline: 'Press start. Find your people.',
    description: 'Players, streamers, and builders talking games: strategy, lore, hardware, and launch nights.',
    joined: false, trending: true, recommended: false,
    rules: ['No spoilers without tags', 'No cheating or piracy talk', 'Respect every platform', 'Keep flame wars out'],
    moderators: [{ name: 'TechVisionary', av: 'av-4' }, { name: 'FreeThinker', av: 'av-2' }],
    events: [{ title: 'Launch Night Stream', date: 'Jun 5' }, { title: 'Community Tournament', date: 'Jun 18' }],
    postIds: ['post-004', 'post-001'],
  },
  {
    id: 'builders-guild', name: 'Builders Guild', category: 'Business',
    members: '54K members', membersNum: 54000, postsCount: '3.9K', online: 480,
    color: '#2f9bff', coverTone: 'b',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#7bc4ff" stroke-width="1.8"><path d="M14 7a4 4 0 0 0-5 5l-5 5 2 2 5-5a4 4 0 0 0 5-5l-2 2-2-2 2-2z"/></svg>',
    tagline: 'Build in the open. Ship with friends.',
    description: 'Founders and makers shipping products, sharing lessons, and finding collaborators.',
    joined: true, trending: false, recommended: true,
    rules: ['Share real lessons', 'No spam or self-promo dumps', 'Help before you pitch', 'Respect IP and credit'],
    moderators: [{ name: 'WayneTech', av: 'av-grad' }, { name: 'OpenMind', av: 'av-3' }],
    events: [{ title: 'Demo Day', date: 'Jun 6' }, { title: 'Founder Office Hours', date: 'Jun 16' }],
    postIds: ['post-005', 'post-004', 'post-003'],
  },
  {
    id: 'mindful-living', name: 'Mindful Living', category: 'Wellness',
    members: '88K members', membersNum: 88000, postsCount: '2.1K', online: 390,
    color: '#28d17c', coverTone: 'd',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#7be3a8" stroke-width="1.8"><path d="M12 21c-4-2-7-5-7-9a7 7 0 0 1 14 0c0 4-3 7-7 9z"/><path d="M12 21V8"/></svg>',
    tagline: 'Slow down. Show up. Be well.',
    description: 'Habits, mindfulness, and balance: a calmer corner for taking care of yourself.',
    joined: false, trending: false, recommended: true,
    rules: ['Be kind and supportive', 'No medical misinformation', 'No product spam', 'Respect privacy'],
    moderators: [{ name: 'LibertyMind', av: 'av-2' }, { name: 'OpenDialogue', av: 'av-5' }],
    events: [{ title: 'Morning Meditation', date: 'Jun 7' }, { title: 'Digital Detox Day', date: 'Jun 21' }],
    postIds: ['post-002', 'post-003'],
  },
  {
    id: 'soundwave', name: 'Soundwave', category: 'Music',
    members: '117K members', membersNum: 117000, postsCount: '7.2K', online: 1480,
    color: '#8b3bff', coverTone: 'c',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#c08bff" stroke-width="1.8"><path d="M9 18V6l10-2v12"/><circle cx="6.5" cy="18" r="2.5"/><circle cx="16.5" cy="16" r="2.5"/></svg>',
    tagline: 'Turn it up. Share your sound.',
    description: "Producers, listeners, and crate-diggers sharing tracks, gear, and the music that moves them.",
    joined: false, trending: false, recommended: true,
    rules: ['Credit the artist', 'No piracy links', 'Tag explicit content', "Hype, don't hate"],
    moderators: [{ name: 'FreeThinker', av: 'av-4' }, { name: 'OpenDialogue', av: 'av-5' }],
    events: [{ title: 'Listening Party', date: 'Jun 9' }, { title: 'Producer Beat Battle', date: 'Jun 22' }],
    postIds: ['post-001', 'post-005'],
  },
];

export const seedCommunityInvites: CommunityInvite[] = [
  {
    id: 'designers-den', name: 'Designers Den', members: '54K members',
    color: '#7a3bff',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#c08bff" stroke-width="1.8"><path d="M12 3 4 8v8l8 5 8-5V8z"/><path d="m4 8 8 5 8-5M12 13v8"/></svg>',
  },
  {
    id: 'crypto-collective', name: 'Crypto Collective', members: '88K members',
    color: '#28d17c',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#7be3a8" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><path d="M9 8h4a2 2 0 0 1 0 4H9m0 0h5a2 2 0 0 1 0 4H9m2-10v12"/></svg>',
  },
  {
    id: 'photo-pros', name: 'Photo Pros', members: '31K members',
    color: '#2f9bff',
    icon: '<svg viewBox="0 0 24 24" fill="none" stroke="#7bc4ff" stroke-width="1.8"><rect x="3" y="7" width="18" height="13" rx="2"/><circle cx="12" cy="13" r="3.5"/><path d="M8 7l1.5-2h5L16 7"/></svg>',
  },
];
